<template>
  <div class="global-filter-bar">
    <b-row>
      <b-col cols="12" md="3">
        <b-form-group label="Date From:" label-for="date-from">
          <b-form-input
            id="date-from"
            v-model="filters.date_from"
            type="date"
            size="sm"
            @change="emitFilters"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Date To:" label-for="date-to">
          <b-form-input
            id="date-to"
            v-model="filters.date_to"
            type="date"
            size="sm"
            @change="emitFilters"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Branch:" label-for="branch-select">
          <b-form-select
            id="branch-select"
            v-model="filters.branch_id"
            :options="branchOptions"
            :disabled="loadingOptions"
            size="sm"
            @change="emitFilters"
          >
            <template #first>
              <option :value="null">Select Branch</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Project:" label-for="project-select">
          <b-form-select
            id="project-select"
            v-model="filters.project_id"
            :options="projectOptions"
            :disabled="loadingOptions"
            size="sm"
            @change="emitFilters"
          >
            <template #first>
              <option :value="null">Select Project</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Employee/Team:" label-for="employee-select">
          <multiselect
            id="employee-select"
            v-model="selectedEmployees"
            :options="employeeOptionsList"
            label="displayName"
            track-by="id"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :searchable="true"
            :disabled="loadingOptions"
            placeholder="Select Employee/Team"
            selected-label=""
            select-label=""
            deselect-label=""
            @input="onEmployeesChanged"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Lead Source:" label-for="lead-source-select">
          <b-form-select
            id="lead-source-select"
            v-model="filters.lead_source_id"
            :options="leadSourceOptions"
            :disabled="loadingOptions"
            size="sm"
            @change="emitFilters"
          >
            <template #first>
              <option :value="null">Select Lead Source</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col v-if="showChannelPartnerFilter" cols="12" md="3">
        <b-form-group label="Channel Partner:" label-for="channel-partner-select">
          <b-form-select
            id="channel-partner-select"
            v-model="filters.channel_partner_id"
            :options="channelPartnerOptions"
            :disabled="loadingOptions"
            size="sm"
          >
            <template #first>
              <option :value="null">All Channel Partners</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3" class="d-flex align-items-end">
        <b-button variant="primary" size="sm" class="mb-1" @click="handleSearch">
          Search
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BRow, BCol, BFormGroup, BFormInput, BFormSelect, BButton } from 'bootstrap-vue';
import Multiselect from 'vue-multiselect';
import moment from 'moment';
import axios from 'axios';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: 'GlobalFilterBar',
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    Multiselect,
  },
  props: {
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
    /** Show Channel Partner filter (CP dashboard). Search uses parent + all downline CPs. */
    showChannelPartnerFilter: {
      type: Boolean,
      default: false,
    },
    /** When set (CP login), always send this id — parent + downline resolved on server. */
    channelPartnerScopeId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      filters: {
        date_from: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        date_to: moment().endOf('day').format('YYYY-MM-DD'),
        branch_id: null,
        project_id: null,
        lead_source_id: null,
        channel_partner_id: null,
      },
      selectedEmployees: [],
      employeeOptionsList: [],
      branches: [],
      projects: [],
      leadSources: [],
      channelPartners: [],
      loadingOptions: false,
    };
  },
  computed: {
    branchOptions() {
      return (this.branches || []).map((b) => ({ value: b.id, text: b.name || '' }));
    },
    projectOptions() {
      return (this.projects || []).map((p) => ({
        value: p.id,
        text: p.projectname || p.name || '',
      }));
    },
    leadSourceOptions() {
      return (this.leadSources || []).map((s) => ({ value: s.id, text: s.name || '' }));
    },
    channelPartnerOptions() {
      return (this.channelPartners || []).map((cp) => ({
        value: cp.id,
        text: this.channelPartnerDisplayName(cp),
      }));
    },
  },
  mounted() {
    this.fetchFilterData();
    this.emitFilters();
  },
  methods: {
    getHeaders() {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
      };
    },
    employeeDisplayName(e) {
      return e.fullname || [e.name, e.surname].filter(Boolean).join(' ') || e.name || '';
    },
    normalizeEmployee(e) {
      const id = Number(e.id);
      return {
        id: Number.isFinite(id) ? id : e.id,
        displayName: this.employeeDisplayName(e),
      };
    },
    channelPartnerDisplayName(cp) {
      return cp.fullname || [cp.name, cp.surname].filter(Boolean).join(' ') || cp.name || `CP #${cp.id}`;
    },
    buildFilterPayload() {
      const employeeIds = (this.selectedEmployees || [])
        .map((emp) => Number(emp.id))
        .filter((id) => Number.isFinite(id) && id > 0);

      const payload = { ...this.filters };
      if (employeeIds.length > 0) {
        payload.employee_ids = employeeIds;
      }
      const cpId = Number(payload.channel_partner_id);
      const lockedCpId = Number(this.channelPartnerScopeId);
      if (this.showChannelPartnerFilter && Number.isFinite(cpId) && cpId > 0) {
        payload.channel_partner_id = cpId;
      } else if (!this.showChannelPartnerFilter && Number.isFinite(lockedCpId) && lockedCpId > 0) {
        payload.channel_partner_id = lockedCpId;
      } else {
        delete payload.channel_partner_id;
      }
      return payload;
    },
    onEmployeesChanged() {
      this.emitFilters();
    },
    async fetchFilterData() {
      if (!this.baseApi) return;
      this.loadingOptions = true;
      try {
        const tasks = [
          this.fetchBranches(),
          this.fetchProjects(),
          this.fetchEmployees(),
          this.fetchLeadSources(),
        ];
        if (this.showChannelPartnerFilter) {
          tasks.push(this.fetchChannelPartners());
        }
        await Promise.all(tasks);
      } catch (e) {
        console.error('Error loading dashboard filter options:', e);
      } finally {
        this.loadingOptions = false;
      }
    },
    async fetchBranches() {
      try {
        const res = await axios.get(`${this.baseApi}/getBranch`, { headers: this.getHeaders() });
        const raw = res.data && res.data.data;
        this.branches = Array.isArray(raw) ? raw : [];
      } catch (e) {
        console.error('Error fetching branches:', e);
        this.branches = [];
      }
    },
    async fetchProjects() {
      try {
        const res = await axios.get(`${this.baseApi}/getallproject`, { headers: this.getHeaders() });
        const raw = res.data && res.data.data;
        this.projects = Array.isArray(raw) ? raw : [];
      } catch (e) {
        console.error('Error fetching projects:', e);
        this.projects = [];
      }
    },
    async fetchEmployees() {
      try {
        const res = await axios.get(`${this.baseApi}/getEmployee`, { headers: this.getHeaders() });
        const raw = res.data && res.data.data;
        const previousIds = (this.selectedEmployees || []).map((e) => Number(e.id));
        this.employeeOptionsList = (Array.isArray(raw) ? raw : [])
          .map((e) => this.normalizeEmployee(e))
          .filter((e) => e.id !== null && e.id !== undefined && e.id !== '');
        if (previousIds.length) {
          const idSet = new Set(previousIds);
          this.selectedEmployees = this.employeeOptionsList.filter((e) => idSet.has(Number(e.id)));
        }
      } catch (e) {
        console.error('Error fetching employees:', e);
        this.employeeOptionsList = [];
      }
    },
    async fetchLeadSources() {
      try {
        const res = await axios.get(`${this.baseApi}/leadsourcedata`, { headers: this.getHeaders() });
        const raw = res.data && res.data.data;
        this.leadSources = Array.isArray(raw) ? raw : [];
      } catch (e) {
        console.error('Error fetching lead sources:', e);
        this.leadSources = [];
      }
    },
    async fetchChannelPartners() {
      try {
        const res = await axios.get(`${this.baseApi}/getallcp`, { headers: this.getHeaders() });
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        const seen = new Set();
        this.channelPartners = [];
        list.forEach((cp) => {
          const id = Number(cp.id);
          if (!Number.isFinite(id) || id <= 0 || seen.has(id)) {
            return;
          }
          seen.add(id);
          this.channelPartners.push(cp);
        });
        this.channelPartners.sort((a, b) =>
          this.channelPartnerDisplayName(a).localeCompare(this.channelPartnerDisplayName(b))
        );
      } catch (e) {
        console.error('Error fetching channel partners:', e);
        this.channelPartners = [];
      }
    },
    emitFilters() {
      this.$emit('filters-changed', this.buildFilterPayload());
    },
    handleSearch() {
      const payload = this.buildFilterPayload();
      this.$emit('filters-changed', payload);
      this.$emit('search', payload);
    },
  },
};
</script>

<style scoped>
.global-filter-bar {
  padding: 15px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #eee;
  margin-bottom: 20px;
}

.global-filter-bar ::v-deep .multiselect {
  min-height: 31px;
  font-size: 0.875rem;
}

.global-filter-bar ::v-deep .multiselect__tags {
  min-height: 31px;
  padding-top: 4px;
}

.global-filter-bar ::v-deep .multiselect__content-wrapper {
  z-index: 1100;
}
</style>
